import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { useId, useState } from 'react'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'

interface WorkCardProps {
    project: ProjectSummary
    className?: string
}

const VIEW_WIDTH = 141.56
const VIEW_HEIGHT = 100

export const WorkCard: FC<WorkCardProps> = ({ project, className }): ReactNode => {
    const [active, setActive] = useState(false)
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const maskId = `reveal-mask-${uid}`
    const filterId = `reveal-distort-${uid}`
    const revealImage = project.imageOne ?? project.cover

    return (
        <Link
            href={`/work/${project.slug}`}
            onMouseEnter={() => setActive(true)}
            onMouseLeave={() => setActive(false)}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            className={cn('group/card flex w-full flex-col gap-4', className)}
        >
            <div className='relative aspect-[1.41562] w-full overflow-clip bg-muted'>
                {revealImage && (
                    <img
                        src={revealImage}
                        alt=''
                        loading='lazy'
                        decoding='async'
                        className='absolute inset-0 size-full scale-105 object-cover object-[50%_35%] transition-transform duration-[1200ms] ease-studio group-hover/card:scale-100'
                    />
                )}

                {project.cover && (
                    <svg
                        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
                        preserveAspectRatio='none'
                        role='img'
                        aria-label={project.coverAlt ?? project.client}
                        className='pointer-events-none absolute inset-0 z-[1] size-full'
                    >
                        <defs>
                            <filter id={filterId} x='-20%' y='-20%' width='140%' height='140%'>
                                <feTurbulence type='fractalNoise' baseFrequency='0.04' numOctaves={2} seed={3} result='noise' />
                                <feDisplacementMap in='SourceGraphic' in2='noise' scale={22} xChannelSelector='R' yChannelSelector='G' />
                            </filter>

                            <mask id={maskId} maskUnits='userSpaceOnUse' x='0' y='0' width={VIEW_WIDTH} height={VIEW_HEIGHT}>
                                <rect width={VIEW_WIDTH} height={VIEW_HEIGHT} fill='white' />

                                <motion.circle
                                    cx={VIEW_WIDTH}
                                    cy={VIEW_HEIGHT}
                                    fill='black'
                                    filter={`url(#${filterId})`}
                                    initial={{ r: 0 }}
                                    animate={{ r: active ? 200 : 0 }}
                                    transition={{ duration: active ? 1.1 : 0.8, ease: [0.22, 1, 0.36, 1] }}
                                />
                            </mask>
                        </defs>

                        <image
                            href={project.cover}
                            width={VIEW_WIDTH}
                            height={VIEW_HEIGHT}
                            preserveAspectRatio='xMidYMid slice'
                            mask={`url(#${maskId})`}
                        />
                    </svg>
                )}

                <div
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 transition-opacity duration-700 ease-studio group-hover/card:opacity-100 group-focus-visible/card:opacity-100'
                />

                <div className='absolute inset-x-10 bottom-[-109px] z-[2] flex flex-col items-center gap-2.5 transition-[bottom] duration-700 ease-studio group-hover/card:bottom-[19px] group-focus-visible/card:bottom-[19px]'>
                    <h3
                        data-mdlfy-cms={`projects:${project.id}:title`}
                        className='t-title text-center text-cream'
                    >
                        {project.title}
                    </h3>
                </div>
            </div>

            <div className='flex w-full items-start gap-6 text-foreground'>
                <p
                    data-mdlfy-cms={`projects:${project.id}:client`}
                    className='t-italic flex-1 text-left'
                >
                    {project.client}
                </p>

                <p
                    data-mdlfy-cms={`projects:${project.id}:year`}
                    className='t-italic whitespace-nowrap'
                >
                    {project.year}
                </p>
            </div>
        </Link>
    )
}
